"use client";

import { useState, useEffect, Suspense, useRef } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Paper,
  Box,
  Group,
  Badge,
  ThemeIcon,
  Loader,
  Button,
  Alert,
  Progress,
  SimpleGrid,
  Divider,
  Image,
  ActionIcon,
  Flex,
  Modal,
  rem,
  Select,
} from "@mantine/core";
import {
  IconPalette,
  IconUser,
  IconPhoto,
  IconShirt,
  IconCheck,
  IconSparkles,
  IconAlertCircle,
  IconUpload,
  IconX,
  IconCamera,
  IconCloudUpload,
  IconArrowRight,
  IconRefresh,
  IconDownload,
} from "@tabler/icons-react";
import { useSearchParams, useRouter } from "next/navigation";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import firebase_app from "@/firebase/config";
import { doc, getFirestore, onSnapshot, setDoc, getDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { getOwnedServices, markServiceOwned, type ServiceId } from "@/lib/ownedServices";
import { trackPurchase } from "@/lib/analytics";


// Types
interface CompletedDimensions {
  colorAnalysis: boolean;
  bodyType: boolean;
  draping: boolean;
  outfits: boolean;
}

interface BundleIntent {
  completed: CompletedDimensions;
  colorSeason?: string;
  kibbeType?: string;
}

interface ServiceStatus {
  status: "pending" | "in_progress" | "complete" | "error";
  progress: number;
  currentStep?: string;
  result?: any;
  error?: string;
}

interface AllServicesStatus {
  colorAnalysis: ServiceStatus;
  bodyType: ServiceStatus;
  draping: ServiceStatus;
  outfits: ServiceStatus;
}

// The 4 dimensions - NOTE: storagePath is where files are uploaded, collection is Firestore
const STYLE_DIMENSIONS = [
  {
    id: "colorAnalysis",
    name: "Color Analysis",
    description: "Discovering your best colors",
    icon: IconPalette,
    color: "teal",
    collection: "generatedColorAnalyses",
    storagePath: "generatedAnalyses",
    usesPhoto: "face" as const,
  },
  {
    id: "draping",
    name: "Digital Draping",
    description: "Generating 144 draped images",
    icon: IconPhoto,
    color: "blue",
    collection: "drapingAnalyses",
    storagePath: "drapingAnalyses",
    storageSubfolder: "uploads",
    usesPhoto: "face" as const,
  },
  {
    id: "bodyType",
    name: "Body Type Analysis",
    description: "Analyzing your body lines",
    icon: IconUser,
    color: "violet",
    collection: "generatedKibbeAnalyses",
    storagePath: "generatedKibbeAnalyses",
    usesPhoto: "fullBody" as const,
  },
  {
    id: "outfits",
    name: "AI Outfit Generator",
    description: "Creating personalized outfits",
    icon: IconShirt,
    color: "pink",
    collection: "generatedOutfits",
    storagePath: "generatedOutfits",
    usesPhoto: "fullBody" as const,
  },
];

// Default style packs for outfit generation
// Must be valid STYLE_PACKS ids (see app/[locale]/ai-outfit-generator/content.tsx)
// so the outfit-wizard can resolve tab names, descriptions, and icons. The old
// values ("casual"/"work"/"elegant") matched no pack id and broke the tabs.
const DEFAULT_STYLE_PACKS = ["casual-everyday", "business-casual", "elevated-minimal"];

type Step = "upload" | "processing" | "results";

function StyleProfileWizardContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const sessionId = searchParams.get("session_id");
  const profileIdFromUrl = searchParams.get("profileId");

  // State
  const [currentStep, setCurrentStep] = useState<Step>(profileIdFromUrl ? "processing" : "upload");
  const [isVerifying, setIsVerifying] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bundleType, setBundleType] = useState<string | null>(null);
  const [bundleIntent, setBundleIntent] = useState<BundleIntent | null>(null);

  // Two separate photo states
  const [facePhoto, setFacePhoto] = useState<File | null>(null);
  const [fullBodyPhoto, setFullBodyPhoto] = useState<File | null>(null);
  const [gender, setGender] = useState<string>("");

  // Camera state
  const [faceCameraOpened, { open: openFaceCamera, close: closeFaceCamera }] = useDisclosure(false);
  const [bodyCameraOpened, { open: openBodyCamera, close: closeBodyCamera }] = useDisclosure(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Stored photos from previous services
  const [storedFacePhoto, setStoredFacePhoto] = useState<string | null>(null);
  const [storedBodyPhoto, setStoredBodyPhoto] = useState<string | null>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [profileSessionId, setProfileSessionId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);

  // Results from earlier purchases (dimension id -> original analysis doc id),
  // so the final output combines ALL owned services, not just this run's
  const [priorResults, setPriorResults] = useState<Record<string, string>>({});

  // Service statuses
  const [servicesStatus, setServicesStatus] = useState<AllServicesStatus>({
    colorAnalysis: { status: "pending", progress: 0 },
    bodyType: { status: "pending", progress: 0 },
    draping: { status: "pending", progress: 0 },
    outfits: { status: "pending", progress: 0 },
  });

  // Track which services need to run
  const [servicesToRun, setServicesToRun] = useState<string[]>(["colorAnalysis", "bodyType", "draping", "outfits"]);

  // Refs to prevent double-processing
  const processingStarted = useRef(false);
  const unsubscribers = useRef<(() => void)[]>([]);

  // Verify payment on mount OR resume from URL profileId
  useEffect(() => {
    // If profileId is in URL, resume that session directly (no payment verification needed)
    if (profileIdFromUrl) {
      setProfileSessionId(profileIdFromUrl);
      setPaymentVerified(true);
      setIsVerifying(false);
      // Only the purchased services have docs for this profile - detect them
      // instead of assuming all 4, otherwise unpurchased ones sit "pending"
      // forever and the page stalls below 100%
      (async () => {
        const firestore = getFirestore(firebase_app);
        const existing = (
          await Promise.all(
            STYLE_DIMENSIONS.map(async (dim) => {
              const snap = await getDoc(doc(firestore, dim.collection, profileIdFromUrl));
              return snap.exists() ? dim.id : null;
            })
          )
        ).filter((id): id is string => id !== null);
        const services = existing.length > 0 ? existing : ["colorAnalysis", "bodyType", "draping", "outfits"];
        setServicesToRun(services);
        startListeningToServices(profileIdFromUrl, services);
      })();
      return;
    }

    // Otherwise, verify payment via session_id
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setIsVerifying(false);
      setError("No session ID provided. Please complete checkout first.");
    }

    // Get bundle intent from sessionStorage (set by bundle page)
    let colorSeason: string | undefined;
    let kibbeType: string | undefined;
    let completedFromIntent: CompletedDimensions | undefined;

    const stored = sessionStorage.getItem("bundleIntent");
    console.log("[Wizard] bundleIntent from sessionStorage:", stored);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        colorSeason = parsed.colorSeason;
        kibbeType = parsed.kibbeType;
        // USE the completed from bundleIntent - this is what the user actually paid for
        if (parsed.completed) {
          completedFromIntent = parsed.completed;
          console.log("[Wizard] completedFromIntent:", completedFromIntent);
        }
      } catch (e) {
        console.error("[Wizard] Failed to parse bundleIntent:", e);
      }
    }

    // Use bundleIntent completed if available, otherwise fall back to localStorage
    const ownedServices = getOwnedServices();
    console.log("[Wizard] ownedServices from localStorage:", ownedServices);

    const completed: CompletedDimensions = completedFromIntent || {
      colorAnalysis: ownedServices.includes("color-analysis"),
      bodyType: ownedServices.includes("kibbe"),
      draping: ownedServices.includes("draping"),
      outfits: ownedServices.includes("outfits"),
    };

    console.log("[Wizard] Final completed object:", completed);
    setBundleIntent({ completed, colorSeason, kibbeType });

    // Determine which services need to run (only those NOT already completed)
    const toRun: string[] = [];
    if (!completed.colorAnalysis) toRun.push("colorAnalysis");
    if (!completed.bodyType) toRun.push("bodyType");
    if (!completed.draping) toRun.push("draping");
    if (!completed.outfits) toRun.push("outfits");

    console.log("[Wizard] servicesToRun:", toRun);
    setServicesToRun(toRun);

    // Check for previously uploaded photos in sessionStorage
    const storedPhotos = sessionStorage.getItem("uploadedPhotos");
    if (storedPhotos) {
      try {
        const photos = JSON.parse(storedPhotos);
        if (photos.length > 0) {
          setStoredFacePhoto(photos[0]); // First photo as face
        }
      } catch {
        // Ignore
      }
    }

    return () => {
      unsubscribers.current.forEach(unsub => unsub());
      stopCamera();
    };
  }, [sessionId, profileIdFromUrl]);

  // Look up results from earlier purchases for dimensions not part of this run,
  // matching on the customer email stored on this profile's docs
  useEffect(() => {
    const missingDims = STYLE_DIMENSIONS.filter(dim => !servicesToRun.includes(dim.id));
    if (missingDims.length === 0 || missingDims.length === STYLE_DIMENSIONS.length) return;

    (async () => {
      try {
        const firestore = getFirestore(firebase_app);

        let email = customerEmail;
        if (!email && profileSessionId) {
          for (const dim of STYLE_DIMENSIONS) {
            if (!servicesToRun.includes(dim.id)) continue;
            const snap = await getDoc(doc(firestore, dim.collection, profileSessionId));
            const docEmail = snap.exists() ? snap.data().customerEmail : null;
            if (docEmail) {
              email = docEmail;
              break;
            }
          }
        }
        if (!email) return;

        const response = await fetch("/api/analyses-by-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        if (!response.ok) throw new Error("Failed to look up prior results");
        const data = await response.json();

        const found: Record<string, string> = {};
        missingDims.forEach((dim) => {
          const completed = (data[dim.collection] ?? [])
            .filter((x: { status?: string }) => x.status === "COMPLETE" || x.status === "COMPLETED")
            .sort((a: { createdAt: number | null }, b: { createdAt: number | null }) => (b.createdAt || 0) - (a.createdAt || 0));
          if (completed[0]) found[dim.id] = completed[0].id;
        });
        setPriorResults(found);
      } catch (err) {
        console.error("Error looking up prior results:", err);
      }
    })();
  }, [servicesToRun, customerEmail, profileSessionId]);

  // Where each dimension's results live when viewed by their original doc id
  const dimResultUrl = (dimId: string, id: string) => {
    switch (dimId) {
      case "colorAnalysis": return `/analysis-wizard?analysisId=${id}`;
      case "draping": return `/advanced-draping-wizard?analysisId=${id}`;
      case "bodyType": return `/kibbe-body-type-test/wizard?analysisId=${id}`;
      case "outfits": return `/outfit-wizard?prepay_id=${id}`;
      default: return "#";
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      notifications.show({
        title: "Camera Error",
        message: "Unable to access your camera. Please check permissions.",
        color: "red",
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const takePhoto = (target: "face" | "body") => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      // Validate video dimensions before capture
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        notifications.show({
          title: "Camera Not Ready",
          message: "Please wait for the camera to load and try again.",
          color: "orange",
        });
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          // Validate blob exists and has reasonable size (at least 1KB)
          if (blob && blob.size > 1024) {
            const file = new File([blob], `${target}-selfie-${Date.now()}.png`, { type: "image/png" });
            if (target === "face") {
              setFacePhoto(file);
              closeFaceCamera();
            } else {
              setFullBodyPhoto(file);
              closeBodyCamera();
            }
            stopCamera();
          } else {
            notifications.show({
              title: "Capture Failed",
              message: "Failed to capture photo. Please try again.",
              color: "red",
            });
          }
        }, "image/png");
      }
    }
  };

  const handleOpenFaceCamera = () => {
    openFaceCamera();
    setTimeout(() => startCamera(), 100);
  };

  const handleOpenBodyCamera = () => {
    openBodyCamera();
    setTimeout(() => startCamera(), 100);
  };

  const handleCloseFaceCamera = () => {
    stopCamera();
    closeFaceCamera();
  };

  const handleCloseBodyCamera = () => {
    stopCamera();
    closeBodyCamera();
  };

  // Convert data URL to File
  const dataUrlToFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const useStoredFacePhoto = () => {
    if (storedFacePhoto) {
      const file = dataUrlToFile(storedFacePhoto, "reused-face-photo.png");
      setFacePhoto(file);
    }
  };

  const verifyPayment = async (stripeSessionId: string) => {
    try {
      const response = await axios.get(`/api/stripe/verify-session?session_id=${stripeSessionId}`);
      if (response.data.status === "complete") {
        trackPurchase("style-profile-bundle", stripeSessionId);
        setPaymentVerified(true);
        setCustomerEmail(response.data.customerEmail);
        if (response.data.metadata?.type) {
          setBundleType(response.data.metadata.type);
        }

        // Only restore from sessionStorage if it's the SAME Stripe session (page refresh)
        // NOT if it's a new payment (which would have a different session_id)
        const storedStripeSession = sessionStorage.getItem("wizardStripeSessionId");
        const storedProfileId = sessionStorage.getItem("wizardProfileSessionId");

        // Check if this is the same Stripe session (page refresh) or a new payment
        const isSameSession = storedStripeSession === stripeSessionId;

        // Use stored profileId only if same session, otherwise check Stripe metadata
        const existingProfileId = isSameSession
          ? storedProfileId
          : response.data.metadata?.profileSessionId;

        if (existingProfileId) {
          setProfileSessionId(existingProfileId);
          await checkExistingProgress(existingProfileId);
        } else {
          // New session - clear any old cached data
          sessionStorage.removeItem("wizardProfileSessionId");
          sessionStorage.removeItem("wizardStripeSessionId");
        }

        // Store current Stripe session for future page refresh detection
        sessionStorage.setItem("wizardStripeSessionId", stripeSessionId);
      } else {
        setError("Payment not verified. Please try again or contact support.");
      }
    } catch (err) {
      console.error("Error verifying payment:", err);
      setError("Error verifying payment. Please contact support.");
    } finally {
      setIsVerifying(false);
    }
  };

  // Build one clean, UNBRANDED PDF combining every purchased service in this
  // profile. Used by professionals who deliver the report to their own clients,
  // so it carries no ColorAnalysisTech branding. Reads the source docs fresh
  // (the hub's per-service state only tracks status, not full payloads) so the
  // PDF always reflects complete data.
  // Generate one clean, UNBRANDED PDF combining every purchased service.
  // Done server-side (GCP function) so it can read image bytes straight from
  // Storage — no signed-URL expiry, no browser CORS, and the 144-image draping
  // set embeds reliably. The function returns a short-lived signed URL we fetch
  // and download.
  const handleExportPdf = async () => {
    if (!profileSessionId || isExporting) return;
    setIsExporting(true);
    try {
      const functions = getFunctions(firebase_app);
      if (process.env.NODE_ENV !== "production") {
        connectFunctionsEmulator(functions, "localhost", 5001);
      }
      const generatePdf = httpsCallable<{ profileId: string }, { url: string }>(
        functions,
        "generateStyleProfilePdf",
        { timeout: 540000 }
      );
      const res = await generatePdf({ profileId: profileSessionId });
      const url = res.data?.url;
      if (!url) throw new Error("No PDF URL returned");

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch the generated PDF");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "Style-Profile.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Error exporting style profile PDF:", err);
      notifications.show({
        color: "red",
        title: "Export failed",
        message: "We couldn't generate the PDF. Please try again in a moment.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const checkExistingProgress = async (profileId: string) => {
    const firestore = getFirestore(firebase_app);

    for (const dim of STYLE_DIMENSIONS) {
      if (!servicesToRun.includes(dim.id)) continue;

      const docRef = doc(firestore, dim.collection, profileId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.status === "IN_PROGRESS" || data.status === "COMPLETE" || data.status === "COMPLETED") {
          setCurrentStep("processing");
          startListeningToServices(profileId);
          return;
        }
      }
    }
  };

  const generateProfileSessionId = () => {
    return `profile_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  };

  // Check if we need full body photo (kibbe or outfits in bundle)
  const needsFullBodyPhoto = servicesToRun.includes("bodyType") || servicesToRun.includes("outfits");

  const handleStartAnalysis = async () => {
    if (!facePhoto) {
      notifications.show({
        title: "Face Photo Required",
        message: "Please upload a face photo to continue.",
        color: "red",
      });
      return;
    }

    const needsGender = servicesToRun.includes("bodyType") || servicesToRun.includes("outfits");
    if (needsGender && !gender) {
      notifications.show({
        title: "Please select gender",
        message: "We use this to style outfits and body-type analysis correctly.",
        color: "red",
      });
      return;
    }

    setIsUploading(true);

    try {
      const newProfileId = generateProfileSessionId();
      setProfileSessionId(newProfileId);
      // Persist to sessionStorage for page refresh recovery
      // Store both profile ID and Stripe session so we can match them on refresh
      sessionStorage.setItem("wizardProfileSessionId", newProfileId);
      if (sessionId) {
        sessionStorage.setItem("wizardStripeSessionId", sessionId);
      }

      const firestore = getFirestore(firebase_app);
      const storage = getStorage(firebase_app);

      // First, upload all photos in parallel (like other services)
      const uploadPromises: Promise<any>[] = [];

      for (const dim of STYLE_DIMENSIONS) {
        if (!servicesToRun.includes(dim.id)) continue;

        // Determine which photo to use for this service
        const photoToUse = dim.usesPhoto === "face" ? facePhoto : (fullBodyPhoto || facePhoto);
        if (!photoToUse) continue;

        // Format: {storagePath}/{id}/{subfolder?}/image_{N}.png (1-indexed)
        const subfolder = (dim as any).storageSubfolder ? `/${(dim as any).storageSubfolder}` : "";
        const storagePath = `${(dim as any).storagePath}/${newProfileId}${subfolder}/image_1.png`;
        const storageRef = ref(storage, storagePath);

        // Upload with explicit content type metadata (matching how other services work)
        const contentType = photoToUse.type || "image/png";
        uploadPromises.push(
          uploadBytes(storageRef, photoToUse, { contentType })
            .then(() => console.log(`Uploaded to ${storagePath}`))
            .catch((err) => {
              console.error(`Failed to upload to ${storagePath}:`, err);
              throw err;
            })
        );
      }

      // Wait for all uploads to complete before creating Firestore docs
      await Promise.all(uploadPromises);

      // Now create Firestore documents for each service
      for (const dim of STYLE_DIMENSIONS) {
        if (!servicesToRun.includes(dim.id)) continue;

        const docRef = doc(firestore, dim.collection, newProfileId);
        const baseDoc = {
          analysisId: newProfileId,
          createdAt: new Date(),
          customerEmail: customerEmail,
          status: "IN_PROGRESS",
          photoCount: 1,
          bundleType: bundleType,
          stripeSessionId: sessionId,
        };

        // Service-specific fields
        let serviceSpecificFields = {};
        if (dim.id === "colorAnalysis") {
          serviceSpecificFields = {
            selectedTier: "full",
          };
        } else if (dim.id === "bodyType") {
          serviceSpecificFields = {
            selectedTier: "full",
            formData: {
              height: "average",
              gender: gender || "female",
            },
            includeAnalysis: false,
          };
        } else if (dim.id === "draping") {
          serviceSpecificFields = {
            selectedTier: "full",
          };
        } else if (dim.id === "outfits") {
          serviceSpecificFields = {
            sessionId: newProfileId,
            stylePacks: DEFAULT_STYLE_PACKS,
            colorSeason: bundleIntent?.colorSeason || null,
            kibbeType: bundleIntent?.kibbeType || null,
            gender: gender || "female",
            includeColorAnalysis: false,
          };
        }

        await setDoc(docRef, { ...baseDoc, ...serviceSpecificFields });
      }

      sessionStorage.removeItem("bundleIntent");

      // Update URL with profileId for page refresh recovery
      const newUrl = `/style-profile-wizard?profileId=${newProfileId}`;
      window.history.replaceState({}, "", newUrl);

      setCurrentStep("processing");
      await startAllServices(newProfileId);
      startListeningToServices(newProfileId);

    } catch (err) {
      console.error("Error starting analysis:", err);
      notifications.show({
        title: "Error",
        message: "Failed to start analysis. Please try again.",
        color: "red",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const startAllServices = async (profileId: string) => {
    if (processingStarted.current) return;
    processingStarted.current = true;

    const functions = getFunctions(firebase_app);
    const isEmulator = process.env.NODE_ENV !== "production";
    if (isEmulator) {
      connectFunctionsEmulator(functions, "localhost", 5001);
    }

    const newStatus = { ...servicesStatus };
    servicesToRun.forEach(serviceId => {
      newStatus[serviceId as keyof AllServicesStatus] = { status: "in_progress", progress: 0 };
    });
    setServicesStatus(newStatus);

    const promises: Promise<any>[] = [];

    // fromBundle: true tells backend to skip individual emails - we show results in wizard
    if (servicesToRun.includes("colorAnalysis")) {
      const analysisFunction = httpsCallable(functions, "analysisFunction");
      promises.push(
        analysisFunction({
          analysisId: profileId,
          plan: "full",
          includeDraping: false,
          fromBundle: true,
          skipEmail: true,
        }).catch(err => console.error("Color analysis error:", err))
      );
    }

    if (servicesToRun.includes("bodyType")) {
      const kibbeFunction = httpsCallable(functions, "kibbeAnalysisFunction");
      promises.push(
        kibbeFunction({
          analysisId: profileId,
          includeAnalysis: false,
          fromBundle: true,
          skipEmail: true,
        }).catch(err => console.error("Kibbe analysis error:", err))
      );
    }

    if (servicesToRun.includes("draping")) {
      const drapingFunction = httpsCallable(functions, "advancedDrapingFunction");
      promises.push(
        drapingFunction({
          analysisId: profileId,
          includeAnalysis: false,
          fromBundle: true,
          skipEmail: true,
          // Don't pass email to avoid individual emails
        }).catch(err => console.error("Draping error:", err))
      );
    }

    if (servicesToRun.includes("outfits")) {
      const outfitFunction = httpsCallable(functions, "outfitGeneratorFunction");
      promises.push(
        outfitFunction({
          sessionId: profileId,
          stylePacks: DEFAULT_STYLE_PACKS,
          colorSeason: bundleIntent?.colorSeason || null,
          kibbeType: bundleIntent?.kibbeType || null,
          gender: gender || "female",
          includeColorAnalysis: false,
          fromBundle: true,
          skipEmail: true,
        }).catch(err => console.error("Outfit generator error:", err))
      );
    }

    Promise.all(promises).catch(err => {
      console.error("Error in service calls:", err);
    });
  };

  const startListeningToServices = (profileId: string, services: string[] = servicesToRun) => {
    const firestore = getFirestore(firebase_app);

    for (const dim of STYLE_DIMENSIONS) {
      if (!services.includes(dim.id)) continue;

      const docRef = doc(firestore, dim.collection, profileId);

      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();

          setServicesStatus(prev => {
            const newStatus = { ...prev };
            const serviceKey = dim.id as keyof AllServicesStatus;

            let status: ServiceStatus["status"] = "pending";
            if (data.status === "IN_PROGRESS" || data.status === "PENDING") {
              status = "in_progress";
            } else if (data.status === "COMPLETE" || data.status === "COMPLETED") {
              status = "complete";
              const serviceIdMap: Record<string, ServiceId> = {
                colorAnalysis: "color-analysis",
                bodyType: "kibbe",
                draping: "draping",
                outfits: "outfits",
              };
              markServiceOwned(serviceIdMap[dim.id]);
            } else if (data.status === "ERROR") {
              status = "error";
            }

            newStatus[serviceKey] = {
              status,
              progress: data.progress || (status === "complete" ? 100 : 0),
              currentStep: data.currentStep,
              result: data.results || data.result || data.outfitImages,
              error: data.error,
            };

            return newStatus;
          });
        }
      }, (err) => {
        console.error(`Error listening to ${dim.id}:`, err);
      });

      unsubscribers.current.push(unsubscribe);
    }
  };

  const allServicesComplete = servicesToRun.every(
    serviceId => servicesStatus[serviceId as keyof AllServicesStatus].status === "complete"
  );

  // Check for partial completion (some done, some errored)
  const hasAnyComplete = servicesToRun.some(
    serviceId => servicesStatus[serviceId as keyof AllServicesStatus].status === "complete"
  );
  const hasAnyError = servicesToRun.some(
    serviceId => servicesStatus[serviceId as keyof AllServicesStatus].status === "error"
  );
  const allDone = servicesToRun.every(
    serviceId => {
      const status = servicesStatus[serviceId as keyof AllServicesStatus].status;
      return status === "complete" || status === "error";
    }
  );

  const overallProgress = servicesToRun.length > 0
    ? servicesToRun.reduce((sum, serviceId) => {
        const service = servicesStatus[serviceId as keyof AllServicesStatus];
        return sum + (service.status === "complete" ? 100 : service.progress);
      }, 0) / servicesToRun.length
    : 0;

  // Render loading state
  if (isVerifying) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" justify="center" h={400}>
          <Loader color="teal" size="xl" />
          <Text c="dimmed" fz="lg">Verifying your payment...</Text>
        </Stack>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
          {error}
        </Alert>
        <Button component="a" href="/style-profile-bundle" mt="lg" variant="outline">
          Return to Bundle Page
        </Button>
      </Container>
    );
  }

  // Render upload step
  if (currentStep === "upload") {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          {/* Header */}
          <Stack align="center" ta="center">
            <Badge size="lg" color="teal" variant="light">Complete Style Profile</Badge>
            <Title order={1} fz={{ base: "xl", md: 28 }}>
              Upload Your Photos
            </Title>
            <Text c="dimmed" fz="md" maw={500}>
              We'll analyze your colors, body type, and create personalized outfit visualizations.
            </Text>
          </Stack>

          {/* Photo Upload Cards */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            {/* Face Photo - Required */}
            <Paper withBorder p="xl" radius="md">
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={600}>Face Photo</Text>
                  <Badge color="red" variant="light" size="sm">Required</Badge>
                </Group>
                <Text fz="sm" c="dimmed">
                  Clear selfie showing your face. Natural lighting preferred. Used for color analysis & draping.
                </Text>

                {!facePhoto ? (
                  <Stack gap="sm">
                    {/* Re-use stored photo option */}
                    {storedFacePhoto && (
                      <Button
                        variant="light"
                        color="teal"
                        leftSection={<IconRefresh size={18} />}
                        onClick={useStoredFacePhoto}
                        fullWidth
                      >
                        Use Previously Uploaded Photo
                      </Button>
                    )}

                    {/* Camera button */}
                    <Button
                      variant="light"
                      color="teal"
                      leftSection={<IconCamera size={18} />}
                      onClick={handleOpenFaceCamera}
                      fullWidth
                    >
                      Take a Selfie
                    </Button>

                    <Divider label="or upload" labelPosition="center" />

                    <Group gap="md" align="stretch">
                      <Box style={{ flex: 1 }}>
                        <Dropzone
                          onDrop={(files) => setFacePhoto(files[0])}
                          maxSize={10 * 1024 ** 2}
                          accept={IMAGE_MIME_TYPE}
                          multiple={false}
                          mih={120}
                          h="100%"
                        >
                          <Flex direction="column" align="center" justify="center" h={120} style={{ pointerEvents: "none" }}>
                            <Dropzone.Accept>
                              <IconUpload style={{ width: rem(32), height: rem(32), color: "var(--mantine-color-teal-6)" }} stroke={1.5} />
                            </Dropzone.Accept>
                            <Dropzone.Reject>
                              <IconX style={{ width: rem(32), height: rem(32), color: "var(--mantine-color-red-6)" }} stroke={1.5} />
                            </Dropzone.Reject>
                            <Dropzone.Idle>
                              <IconCloudUpload style={{ width: rem(32), height: rem(32), color: "var(--mantine-color-gray-5)" }} stroke={1.5} />
                            </Dropzone.Idle>
                            <Text size="xs" ta="center" mt="xs">
                              Drop photo or click to select
                            </Text>
                          </Flex>
                        </Dropzone>
                      </Box>
                      <Box w={90} style={{ flexShrink: 0 }}>
                        <Text fz="xs" c="dimmed" mb={4} ta="center">Example:</Text>
                        <Image
                          src="/profile-wizard/face.webp"
                          alt="Example face photo"
                          radius="md"
                          h={100}
                          fit="cover"
                          style={{ border: "1px solid var(--mantine-color-gray-3)" }}
                        />
                      </Box>
                    </Group>
                  </Stack>
                ) : (
                  <Box pos="relative">
                    <Image
                      src={URL.createObjectURL(facePhoto)}
                      alt="Face photo"
                      radius="md"
                      h={180}
                      fit="contain"
                    />
                    <ActionIcon
                      variant="filled"
                      pos="absolute"
                      top={rem(8)}
                      right={rem(8)}
                      color="red"
                      size="sm"
                      radius="xl"
                      onClick={() => setFacePhoto(null)}
                    >
                      <IconX style={{ width: rem(14), height: rem(14) }} />
                    </ActionIcon>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Full Body Photo - Optional */}
            <Paper withBorder p="xl" radius="md" style={{ opacity: needsFullBodyPhoto ? 1 : 0.6 }}>
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={600}>Full Body Photo</Text>
                  <Badge color="gray" variant="light" size="sm">Optional</Badge>
                </Group>
                <Text fz="sm" c="dimmed">
                  Standing photo showing your full body. Improves accuracy for body type analysis & outfit generation.
                </Text>

                {!fullBodyPhoto ? (
                  needsFullBodyPhoto ? (
                    <Stack gap="sm">
                      {/* Camera button */}
                      <Button
                        variant="light"
                        color="teal"
                        leftSection={<IconCamera size={18} />}
                        onClick={handleOpenBodyCamera}
                        fullWidth
                      >
                        Take a Photo
                      </Button>

                      <Divider label="or upload" labelPosition="center" />

                      <Group gap="md" align="stretch">
                        <Box style={{ flex: 1 }}>
                          <Dropzone
                            onDrop={(files) => setFullBodyPhoto(files[0])}
                            maxSize={10 * 1024 ** 2}
                            accept={IMAGE_MIME_TYPE}
                            multiple={false}
                            mih={120}
                            h="100%"
                          >
                            <Flex direction="column" align="center" justify="center" h={120} style={{ pointerEvents: "none" }}>
                              <Dropzone.Accept>
                                <IconUpload style={{ width: rem(32), height: rem(32), color: "var(--mantine-color-teal-6)" }} stroke={1.5} />
                              </Dropzone.Accept>
                              <Dropzone.Reject>
                                <IconX style={{ width: rem(32), height: rem(32), color: "var(--mantine-color-red-6)" }} stroke={1.5} />
                              </Dropzone.Reject>
                              <Dropzone.Idle>
                                <IconCloudUpload style={{ width: rem(32), height: rem(32), color: "var(--mantine-color-gray-5)" }} stroke={1.5} />
                              </Dropzone.Idle>
                              <Text size="xs" ta="center" mt="xs">
                                Drop photo or click to select
                              </Text>
                            </Flex>
                          </Dropzone>
                        </Box>
                        <Box w={90} style={{ flexShrink: 0 }}>
                          <Text fz="xs" c="dimmed" mb={4} ta="center">Example:</Text>
                          <Image
                            src="/profile-wizard/body.png"
                            alt="Example body photo"
                            radius="md"
                            h={100}
                            fit="cover"
                            style={{ border: "1px solid var(--mantine-color-gray-3)" }}
                          />
                        </Box>
                      </Group>

                      <Text fz="xs" c="dimmed" ta="center">
                        Skip this to use face photo for body type & outfit generation
                      </Text>
                    </Stack>
                  ) : (
                    <Text fz="sm" c="dimmed" ta="center" py="md">
                      Not needed for your bundle
                    </Text>
                  )
                ) : (
                  <Box pos="relative">
                    <Image
                      src={URL.createObjectURL(fullBodyPhoto)}
                      alt="Full body photo"
                      radius="md"
                      h={180}
                      fit="contain"
                    />
                    <ActionIcon
                      variant="filled"
                      pos="absolute"
                      top={rem(8)}
                      right={rem(8)}
                      color="red"
                      size="sm"
                      radius="xl"
                      onClick={() => setFullBodyPhoto(null)}
                    >
                      <IconX style={{ width: rem(14), height: rem(14) }} />
                    </ActionIcon>
                  </Box>
                )}
              </Stack>
            </Paper>
          </SimpleGrid>

          {/* What's Included */}
          <Paper withBorder p="lg" radius="md">
            <Text fw={600} mb="md">What we'll analyze:</Text>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              {STYLE_DIMENSIONS.filter(dim => servicesToRun.includes(dim.id)).map((dim) => (
                <Group key={dim.id} gap="sm" wrap="nowrap">
                  <ThemeIcon size="md" radius="md" variant="light" color={dim.color}>
                    <dim.icon size={16} />
                  </ThemeIcon>
                  <Box>
                    <Text fz="sm" fw={500}>{dim.name}</Text>
                    <Text fz="xs" c="dimmed">
                      {dim.usesPhoto === "face" ? "Uses face photo" : "Uses body photo"}
                    </Text>
                  </Box>
                </Group>
              ))}
            </SimpleGrid>
          </Paper>

          {/* Gender selector (required for body-type / outfits services) */}
          {(servicesToRun.includes("bodyType") || servicesToRun.includes("outfits")) && (
            <Paper withBorder p="md" radius="md">
              <Text fz="sm" fw={600} mb={4}>Style as</Text>
              <Text fz="xs" c="dimmed" mb="sm">
                Used for body-type analysis and outfit styling.
              </Text>
              <Select
                placeholder="Select..."
                data={[
                  { value: "female", label: "Female" },
                  { value: "male", label: "Male" },
                  { value: "non-binary", label: "Non-binary / androgynous" },
                ]}
                value={gender}
                onChange={(val) => setGender(val || "")}
              />
            </Paper>
          )}

          {/* CTA */}
          <Button
            size="lg"
            fullWidth
            color="teal"
            rightSection={<IconArrowRight size={18} />}
            loading={isUploading}
            disabled={!facePhoto}
            onClick={handleStartAnalysis}
          >
            Start My Analysis
          </Button>

          <Text fz="xs" c="dimmed" ta="center">
            {!fullBodyPhoto && needsFullBodyPhoto
              ? "No full body photo? We'll use your face photo for all analyses."
              : "Your photos are processed securely and never shared."
            }
          </Text>
        </Stack>

        {/* Face Camera Modal */}
        <Modal opened={faceCameraOpened} onClose={handleCloseFaceCamera} title="Take a Face Selfie" centered size="md">
          <Stack>
            <Box h={400} style={{ overflow: "hidden", borderRadius: 8 }}>
              <video ref={videoRef} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Box>
            <Button onClick={() => takePhoto("face")} leftSection={<IconCamera size={18} />} color="teal" size="lg">
              Capture Photo
            </Button>
          </Stack>
        </Modal>

        {/* Body Camera Modal */}
        <Modal opened={bodyCameraOpened} onClose={handleCloseBodyCamera} title="Take a Full Body Photo" centered size="md">
          <Stack>
            <Text fz="sm" c="dimmed" mb="xs">
              Position yourself so your full body is visible in frame
            </Text>
            <Box h={400} style={{ overflow: "hidden", borderRadius: 8 }}>
              <video ref={videoRef} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Box>
            <Button onClick={() => takePhoto("body")} leftSection={<IconCamera size={18} />} color="teal" size="lg">
              Capture Photo
            </Button>
          </Stack>
        </Modal>

        {/* Hidden canvas for capturing photos */}
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </Container>
    );
  }

  // Render processing step
  if (currentStep === "processing") {
    return (
      <Container size="md" py="xl">
        <Stack gap="xl">
          {/* Header */}
          <Stack align="center" ta="center">
            <Badge
              size="lg"
              color={allServicesComplete ? "teal" : hasAnyError && allDone ? "orange" : "blue"}
              variant="light"
            >
              {allServicesComplete
                ? "Complete!"
                : hasAnyError && allDone
                  ? "Partially Complete"
                  : "Analyzing..."}
            </Badge>
            <Title order={1} fz={{ base: "xl", md: 28 }}>
              {allServicesComplete
                ? "Your Style Profile is Ready!"
                : hasAnyError && allDone
                  ? "Some Results Are Ready"
                  : "Building Your Style Profile"
              }
            </Title>
            <Progress
              value={overallProgress}
              color="teal"
              size="lg"
              radius="xl"
              w="100%"
              maw={500}
              animated={!allServicesComplete}
            />
            <Text c="dimmed" fz="sm">
              {Math.round(overallProgress)}% complete
            </Text>
          </Stack>

          {/* Service Progress Cards */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {STYLE_DIMENSIONS.filter(dim => servicesToRun.includes(dim.id)).map((dim) => {
              const service = servicesStatus[dim.id as keyof AllServicesStatus];
              const isComplete = service.status === "complete";
              const isInProgress = service.status === "in_progress";
              const isError = service.status === "error";

              return (
                <Paper
                  key={dim.id}
                  p="md"
                  radius="md"
                  withBorder
                  style={{
                    borderColor: isComplete
                      ? `var(--mantine-color-${dim.color}-4)`
                      : isError
                        ? "var(--mantine-color-red-4)"
                        : undefined,
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="md" wrap="nowrap">
                      <ThemeIcon
                        size="lg"
                        radius="md"
                        variant={isComplete || isError ? "filled" : "light"}
                        color={isComplete ? dim.color : isError ? "red" : "gray"}
                      >
                        {isComplete ? (
                          <IconCheck size={18} />
                        ) : isError ? (
                          <IconAlertCircle size={18} />
                        ) : (
                          <dim.icon size={18} />
                        )}
                      </ThemeIcon>
                      <Box>
                        <Text fw={600} fz="sm">{dim.name}</Text>
                        <Text fz="xs" c={isError ? "red" : "dimmed"}>
                          {isComplete
                            ? "Complete!"
                            : isError
                              ? (service.error || "An error occurred")
                              : isInProgress
                                ? (service.currentStep || dim.description)
                                : "Waiting..."
                          }
                        </Text>
                      </Box>
                    </Group>
                    {isInProgress && !isComplete && (
                      <Loader size="sm" color={dim.color} />
                    )}
                  </Group>
                  {isInProgress && service.progress > 0 && service.progress < 100 && (
                    <Progress
                      value={service.progress}
                      color={dim.color}
                      size="xs"
                      radius="xl"
                      mt="sm"
                    />
                  )}
                </Paper>
              );
            })}
            {STYLE_DIMENSIONS.filter(dim => !servicesToRun.includes(dim.id) && priorResults[dim.id]).map((dim) => (
              <Paper
                key={dim.id}
                p="md"
                radius="md"
                withBorder
                style={{ borderColor: `var(--mantine-color-${dim.color}-4)` }}
              >
                <Group gap="md" wrap="nowrap">
                  <ThemeIcon size="lg" radius="md" variant="filled" color={dim.color}>
                    <IconCheck size={18} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={600} fz="sm">{dim.name}</Text>
                    <Text fz="xs" c="dimmed">Complete (from your earlier purchase)</Text>
                  </Box>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>

          {/* View Results - show as soon as ANY service completes */}
          {hasAnyComplete && (
            <Stack gap="md">
              <Divider />
              {allServicesComplete ? (
                <Text ta="center" c="dimmed" fz="sm">
                  All analyses complete! View your personalized results below.
                </Text>
              ) : hasAnyError && allDone ? (
                <Alert color="orange" variant="light" icon={<IconAlertCircle size={16} />}>
                  Some analyses completed successfully, but others encountered errors.
                  You can still view your completed results below.
                </Alert>
              ) : (
                <Text ta="center" c="dimmed" fz="sm">
                  Some results are ready! View them while we finish the rest.
                </Text>
              )}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                {servicesToRun.includes("colorAnalysis") && servicesStatus.colorAnalysis.status === "complete" && (
                  <Button
                    component="a"
                    href={`/analysis-wizard?analysisId=${profileSessionId}`}
                    variant="light"
                    color="teal"
                    leftSection={<IconPalette size={16} />}
                  >
                    View Color Analysis
                  </Button>
                )}
                {servicesToRun.includes("draping") && servicesStatus.draping.status === "complete" && (
                  <Button
                    component="a"
                    href={`/advanced-draping-wizard?analysisId=${profileSessionId}`}
                    variant="light"
                    color="blue"
                    leftSection={<IconPhoto size={16} />}
                  >
                    View Draping
                  </Button>
                )}
                {servicesToRun.includes("bodyType") && servicesStatus.bodyType.status === "complete" && (
                  <Button
                    component="a"
                    href={`/kibbe-body-type-test/wizard?analysisId=${profileSessionId}`}
                    variant="light"
                    color="violet"
                    leftSection={<IconUser size={16} />}
                  >
                    View Body Type
                  </Button>
                )}
                {servicesToRun.includes("outfits") && servicesStatus.outfits.status === "complete" && (
                  <Button
                    component="a"
                    href={`/outfit-wizard?prepay_id=${profileSessionId}`}
                    variant="light"
                    color="pink"
                    leftSection={<IconShirt size={16} />}
                  >
                    View Outfits
                  </Button>
                )}
                {STYLE_DIMENSIONS.filter(dim => !servicesToRun.includes(dim.id) && priorResults[dim.id]).map((dim) => (
                  <Button
                    key={dim.id}
                    component="a"
                    href={dimResultUrl(dim.id, priorResults[dim.id])}
                    variant="light"
                    color={dim.color}
                    leftSection={<dim.icon size={16} />}
                  >
                    View {dim.name}
                  </Button>
                ))}
              </SimpleGrid>

              {/* Unbranded PDF export — one clean report combining every
                  purchased service. For professionals delivering to clients. */}
              {allServicesComplete && (
                <>
                  <Divider label="Or download everything" labelPosition="center" />
                  <Button
                    onClick={handleExportPdf}
                    loading={isExporting}
                    variant="filled"
                    color="dark"
                    leftSection={<IconDownload size={16} />}
                    fullWidth
                  >
                    Export Full Profile (PDF)
                  </Button>
                  <Text ta="center" c="dimmed" fz="xs">
                    A clean, unbranded report.
                  </Text>
                </>
              )}
            </Stack>
          )}

          {/* Error summary with contact info when there are failures */}
          {hasAnyError && allDone && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text fw={500} mb="xs">Some services encountered errors</Text>
              <Text fz="sm" mb="sm">
                Please contact us with your session ID and we'll help resolve this.
              </Text>
              <Button
                component="a"
                href="/contact"
                size="xs"
                variant="light"
                color="red"
              >
                Contact Support
              </Button>
            </Alert>
          )}

          {/* Info while processing - hide when all done (success or error) */}
          {!allDone && (
            <Paper p="lg" radius="md" withBorder>
              <Group gap="sm" mb="sm">
                <IconSparkles size={20} color="var(--mantine-color-teal-6)" />
                <Text fw={600}>This may take a few minutes</Text>
              </Group>
              <Text fz="sm" c="dimmed">
                We're using AI to analyze your photos and generate personalized results.
                Feel free to keep this tab open - we'll show your results as soon as they're ready!
              </Text>
            </Paper>
          )}

          {/* Session Info */}
          <Text fz="xs" c="dimmed" ta="center">
            Session ID: {profileSessionId || sessionId}
          </Text>
        </Stack>
      </Container>
    );
  }

  return null;
}

export default function StyleProfileWizardPage() {
  return (
    <Suspense
      fallback={
        <Container size="md" py="xl">
          <Stack align="center" justify="center" h={400}>
            <Loader color="teal" size="xl" />
            <Text c="dimmed">Loading...</Text>
          </Stack>
        </Container>
      }
    >
      <StyleProfileWizardContent />
    </Suspense>
  );
}
